import React, { forwardRef } from "react";
import type { ButtonHTMLAttributes, CSSProperties, ForwardedRef } from "react";
import cn from "classnames";

import { SpriteIcon } from "@litres-libs/sprite-icons";

import type { IButtonProps, IButtonContentProps } from "../../types";

import styles from "./Button.module.scss";
import { CircleLoader } from "../../../circle-loader";
import { CircleLoaderV2 } from "../../../circle-loader2";
import { Link } from "../../../link";

const stylesStr = styles as unknown as Record<string, string>;
const ButtonContent = ({
  loading,
  success,
  text,
  secondaryText,
  icon,
  iconClassName,
  iconPosition,
  textContainerClassName,
  size,
}: IButtonContentProps) => {
  return (
    <div
      className={cn(styles.buttonContent, {
        [styles.buttonContent_loading]: loading,
        [styles.buttonContent_success]: success,
      })}
    >
      {loading && (
        <div className={styles.buttonLoader}>
          {size === "small" || size === "very-small" ? (
            <CircleLoader />
          ) : (
            <CircleLoaderV2 />
          )}
        </div>
      )}
      {success && (
        <div className={styles.buttonCheckmark}>
          <SpriteIcon
            className={styles.buttonCheckmarkSpriteIcon}
            name="check"
            size={size === "small" || size === "very-small" ? 16 : 24}
          />
        </div>
      )}
      <div
        className={styles.buttonContent__inner}
        data-testid="button__content"
      >
        {icon && iconPosition !== "right" && (
          <div className={cn(styles.buttonIcon, iconClassName)}>{icon}</div>
        )}
        <div className={cn(styles.textContainer, textContainerClassName)}>
          {text}
          {secondaryText && size !== "small" && size !== "very-small" && (
            <div className={styles.secondaryText}>{secondaryText}</div>
          )}
        </div>
        {icon && iconPosition === "right" && (
          <div
            className={cn(
              styles.buttonIcon,
              styles.buttonIcon_right,
              iconClassName
            )}
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

export const Button = forwardRef<HTMLElement, IButtonProps>(
  (
    {
      id,
      type = "button",
      disabled,
      loading,
      success,
      text,
      textContainerClassName,
      secondaryText,
      icon,
      iconClassName,
      iconPosition,
      size,
      mode,
      customColor,
      fullWidth,
      className,
      onClick,
      href,
      style,
      targetBlank,
      analyticsId,
      e2eId,
      tabIndex,
      ...ariaAttributes
    },
    ref
  ) => {
    let buttonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
      type,
      tabIndex,
      ...ariaAttributes,
    };
    buttonProps = disabled ? { ...buttonProps, disabled: true } : buttonProps;
    const buttonContentProps = {
      loading: loading,
      success: success,
      text: text,
      textContainerClassName: textContainerClassName,
      secondaryText: secondaryText,
      icon: icon,
      iconClassName: iconClassName,
      iconPosition: iconPosition,
      size: size,
    };
    const classes = cn(
      styles.button,
      stylesStr[`button_${size}`],
      customColor ? undefined : stylesStr[`button_${mode}`],
      {
        [styles.button_primary]: !!customColor,
        [styles.button_fullWidth]: fullWidth,
        [styles.button_loading]: loading,
        [styles.button_success]: success,
      },
      className
    );
    let customStyles: CSSProperties = style ?? {};
    if (customColor && typeof customColor === "string") {
      customStyles = {
        "--button-background-default": customColor,
        "--button-background-hover": customColor,
        "--button-background-active": customColor,
      } as CSSProperties;
    } else if (typeof customColor !== "string" && customColor?.default) {
      customStyles = {
        "--button-background-default": customColor.default,
        "--button-background-hover": customColor.hover || customColor.default,
        "--button-background-active": customColor.active || customColor.default,
      } as CSSProperties;
      if (customColor.text) {
        customStyles = {
          ...customStyles,
          "--button-text-color": customColor.text,
        } as CSSProperties;
      }
    }
    const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
      if (type === "submit" || type === "reset") {
        return;
      }

      if (typeof onClick === "function") {
        onClick(e);
      }
      if (!e.defaultPrevented && typeof window !== "undefined" && href) {
        e.preventDefault();
        if (targetBlank) {
          window.open(href, "_blank");
        } else {
          window.location.href = href;
        }
      }
    };

    const target = targetBlank ? "_blank" : undefined;

    return href ? (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        href={href}
        className={classes}
        style={customStyles}
        onClick={handleClick}
        target={target}
        data-analytics-id={analyticsId}
        data-testid={e2eId}
        role="button"
        {...ariaAttributes}
        tabIndex={tabIndex}
        id={id}
      >
        <ButtonContent {...buttonContentProps} />
      </Link>
    ) : (
      <button
        ref={ref as ForwardedRef<HTMLButtonElement>}
        {...buttonProps}
        className={classes}
        style={customStyles}
        onClick={handleClick}
        data-analytics-id={analyticsId}
        data-testid={e2eId}
        id={id}
      >
        <ButtonContent {...buttonContentProps} />
      </button>
    );
  }
);

Button.displayName = "Button";
